import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing walkthrough state and navigation
 * @param {Object} config - Walkthrough configuration with steps array
 * @returns {Object} Walkthrough state and control functions
 */
export function useWalkthrough(config) {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Check URL parameters on mount to determine if walkthrough should be active
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const walkthroughParam = urlParams.get('walkthrough') === 'true' || urlParams.get('tour') === 'true';

    if (walkthroughParam && config && config.enabled && config.steps && config.steps.length > 0) {
      setIsActive(true);
      setCurrentStepIndex(0);
    }
  }, [config]);

  // Get current step
  const currentStep = config && config.steps && config.steps[currentStepIndex]
    ? config.steps[currentStepIndex]
    : null;

  // Navigation functions
  const goToStep = useCallback((index) => {
    if (!config || !config.steps) return;

    if (index >= 0 && index < config.steps.length) {
      setCurrentStepIndex(index);
    }
  }, [config]);

  const nextStep = useCallback(() => {
    if (!config || !config.steps) return;
    setCurrentStepIndex(prev => {
      const next = prev + 1;
      return next < config.steps.length ? next : prev;
    });
  }, [config]);

  const previousStep = useCallback(() => {
    setCurrentStepIndex(prev => {
      return prev > 0 ? prev - 1 : prev;
    });
  }, []);

  const closeWalkthrough = useCallback(() => {
    setIsActive(false);
    setCurrentStepIndex(0);
  }, []);

  const startWalkthrough = useCallback(() => {
    if (config && config.enabled && config.steps && config.steps.length > 0) {
      setIsActive(true);
      setCurrentStepIndex(0);
    }
  }, [config]);

  // Keyboard event handling
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsActive(false);
        setCurrentStepIndex(0);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        setCurrentStepIndex(prev => {
          const next = prev + 1;
          return config?.steps && next < config.steps.length ? next : prev;
        });
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        setCurrentStepIndex(prev => prev > 0 ? prev - 1 : prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, config]);

  // Calculate progress
  const totalSteps = config && config.steps ? config.steps.length : 0;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const progress = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

  return {
    // State
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    progress,

    // Actions
    goToStep,
    nextStep,
    previousStep,
    closeWalkthrough,
    startWalkthrough
  };
}
